// src/rig/garmentFit.js
import { GARMENT_COVERAGE } from "../ml/garmentCoverage";

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function mid(a, b) {
  return (Number(a) + Number(b)) * 0.5;
}

/**
 * Compute scale + Y offset so garment fits mannequin.
 *
 * Responsibilities:
 * - Y scaling + vertical placement still comes from GARMENT_COVERAGE (coverage/anchor/topLift)
 * - X/Z scaling comes from structural per-GLB measurements (hipX / shoulderX) when provided
 *
 * Inputs:
 * - category: "top" | "bottom" | ... (must match keys in GARMENT_COVERAGE)
 * - garmentBounds: { min, max, size } in the SAME space used previously
 * - mannequinBounds: { min, max, size }
 * - garmentMetrics: entry from garmentMetrics.json for this specific garment
 *   e.g. { hipX: 0.2841 } or { shoulderX: 0.2626 }
 * - bodyRefs: mannequin structural refs, e.g. { hipX: 0.287169, shoulderX: 0.323299 }
 */
export function computeGarmentFit({
  category,
  variant,
  garmentBounds,
  mannequinBounds,
  garmentMetrics,
  bodyRefs,
}) {
  // HARD GUARDS: never throw from here
  if (!garmentBounds || !mannequinBounds) return null;
  if (!garmentBounds.size || !mannequinBounds.size) return null;
  if (!garmentBounds.min || !garmentBounds.max) return null;
  if (!mannequinBounds.min || !mannequinBounds.max) return null;

const cat = String(category || "").toLowerCase();
let v = variant ? String(variant).toLowerCase() : null;

// 🔥 AUTO-INFER DRESS TYPE IF MISSING
if (!v && cat === "dress" && garmentBounds?.size?.y && mannequinBounds?.size?.y) {
  const ratio = garmentBounds.size.y / mannequinBounds.size.y;

  if (ratio > 0.85) v = "maxi";
  else if (ratio > 0.65) v = "midi";
  else v = "mini";
}

const key = v ? `${cat}:${v}` : cat;

console.log("[FIT INPUT]", {
  category: cat,
  variant_in: variant,
  variant_resolved: v,
  key,
  garmentHeight: garmentBounds?.size?.y,
  mannequinHeight: mannequinBounds?.size?.y,
});

  const rules = GARMENT_COVERAGE[key] || GARMENT_COVERAGE[cat];
  console.log("[rules used]", key, rules);

  if (!rules) return null;


  const manH = mannequinBounds.size.y;
  const gH = garmentBounds.size.y;

  if (!(manH > 0) || !(gH > 0)) return null;

  // -------------------------
  // Y scale from coverage rule
  // -------------------------
  // pick a target coverage inside the contract range
// (0.0 = minCoverage, 1.0 = maxCoverage)
const bias =
  cat === "top"   ? 0.65 :
  cat === "bottom"? 0.55 :
  cat === "dress" ? 0.78 :
  cat === "outer" ? 0.70 :
  0.55;
  
const covNorm = clamp(
  Number(rules.minCoverage) + (Number(rules.maxCoverage) - Number(rules.minCoverage)) * bias,
  0.05,
  0.98
);

  const desiredH = covNorm * manH;
  let scaleY = desiredH / gH;

// --- optional mesh-coverage guardrails ---
// prevents extreme scaling when a GLB bbox is odd
const MESH_MIN = 0.70; // garment ends up at least 70% of its bbox height
const MESH_MAX = 1.20; // and at most 120%
scaleY = clamp(scaleY, (MESH_MIN * manH) / gH, (MESH_MAX * manH) / gH);

  // -----------------------------------------
  // X/Z scale from structural measurements (NEW)
  // -----------------------------------------
let scaleX = scaleY;
let scaleZ = scaleY;


  // Prefer measured structural widths when available.
  // Falls back to 1.0 if metrics are missing (so you can roll out gradually).
  if (cat === "top") {
    const g = garmentMetrics?.shoulderX;
    const b = bodyRefs?.shoulderX;
    if (g && b && g > 0 && b > 0) {
    const ratio = b / g;
scaleX = scaleY * ratio;
scaleZ = scaleX;
    }
  } else if (cat === "bottom") {
    const g = garmentMetrics?.hipX;
    const b = bodyRefs?.hipX;
    if (g && b && g > 0 && b > 0) {
      const ratio = b / g;
      scaleX = scaleY * ratio;
      scaleZ = scaleX;

    }
    } else if (cat === "dress") {
  const g = garmentMetrics?.shoulderX;
  const b = bodyRefs?.shoulderX;
  if (g && b && g > 0 && b > 0) {
    const ratio = b / g;
    scaleX = scaleY * ratio;
    scaleZ = scaleX;
  }

  } else {
    // Optional: if you have other categories later, you can extend here.
    // For now, leave X/Z unchanged.
  }

// -----------------------------------------
// Contract-driven sanity (no hidden magic)
// -----------------------------------------

// Y: clamp via category minimum floors (prevents micro-garments) + optional upper cap
const yMin = Number(rules.minCoverage ?? 0.05) * manH; // already used in desiredH
const yMax = Number(rules.maxCoverage ?? 0.98) * manH;

const minScaleY = (0.85 * yMin) / gH; // allow some wiggle
const maxScaleY = (1.15 * yMax) / gH;

scaleY = clamp(scaleY, minScaleY, maxScaleY);

// X/Z: re-apply width contract around the FINAL scaleY (so it can't blow up)
const wMin = Number(rules.widthMin ?? 0.85);
const wMax = Number(rules.widthMax ?? 1.15);
const mul = clamp(scaleX / scaleY, wMin, wMax);
scaleX = scaleY * mul;
scaleZ = scaleY * mul;

  // -------------------------
  // Vertical placement (anchor)
  // -------------------------
  const manMinY = mannequinBounds.min.y;
  const targetTopY =
    manMinY +
    clamp(rules.anchor, 0.0, 1.0) * manH +
    (rules.topLift ? Number(rules.topLift) * manH : 0);

  const garmentTopY = garmentBounds.max.y;
  const yOffset = targetTopY - garmentTopY * scaleY;

  console.log("[computeGarmentFit widths]", {
  cat,
  gShoulder: garmentMetrics?.shoulderX,
  bShoulder: bodyRefs?.shoulderX,
  rawRatio: garmentMetrics?.shoulderX && bodyRefs?.shoulderX ? (bodyRefs.shoulderX / garmentMetrics.shoulderX) : null,
  scaleX,
  scaleY,
  wMin: rules.widthMin,
  wMax: rules.widthMax,
});

  return { scaleX, scaleY, scaleZ, yOffset, key };
}
